import { useState, type FormEvent } from 'react';

export interface ProfileFormValues {
  displayName: string;
  roomNumber: string;
  allergies: string;
}

interface ProfileFormProps {
  initial: ProfileFormValues;
  submitLabel: string;
  /** Called with trimmed values. The parent handles persistence + errors. */
  onSubmit: (values: ProfileFormValues) => Promise<void>;
  onCancel?: () => void;
  /** Optional context line shown above the name field. */
  hint?: string;
}

const ALLERGIES_MAX = 280;

/**
 * Shared form used by both the first-time profile setup page and the
 * editable profile tab. Name + room number are required; allergies is
 * free text and may be left blank.
 */
export function ProfileForm({ initial, submitLabel, onSubmit, onCancel, hint }: ProfileFormProps) {
  const [displayName, setDisplayName] = useState(initial.displayName);
  const [roomNumber, setRoomNumber] = useState(initial.roomNumber);
  const [allergies, setAllergies] = useState(initial.allergies);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);

    const trimmedName = displayName.trim();
    const trimmedRoom = roomNumber.trim();

    if (trimmedName.length === 0) {
      setError('Name is required.');
      return;
    }
    if (trimmedRoom.length === 0) {
      setError('Room number is required.');
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit({
        displayName: trimmedName,
        roomNumber: trimmedRoom,
        allergies: allergies.trim(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Couldn’t save your profile. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
      {hint !== undefined && <p className="text-sm text-wca-gray-mid">{hint}</p>}

      <div>
        <label htmlFor="displayName" className="block text-sm font-medium mb-1">
          Your name <span className="text-wca-red">*</span>
        </label>
        <input
          id="displayName"
          type="text"
          autoComplete="name"
          required
          maxLength={80}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Jane Carter"
          className="w-full rounded-lg border border-wca-gray-light bg-wca-surface p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wca-red"
        />
      </div>

      <div>
        <label htmlFor="roomNumber" className="block text-sm font-medium mb-1">
          Room number <span className="text-wca-red">*</span>
        </label>
        <input
          id="roomNumber"
          type="text"
          autoComplete="off"
          required
          maxLength={20}
          value={roomNumber}
          onChange={(e) => setRoomNumber(e.target.value)}
          placeholder="e.g. 204"
          className="w-full rounded-lg border border-wca-gray-light bg-wca-surface p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wca-red"
        />
        <p className="mt-1 text-xs text-wca-gray-mid">
          The barista uses this to deliver your order.
        </p>
      </div>

      <div>
        <label htmlFor="allergies" className="block text-sm font-medium mb-1">
          Allergies or dietary notes
        </label>
        <textarea
          id="allergies"
          rows={3}
          maxLength={ALLERGIES_MAX}
          value={allergies}
          onChange={(e) => setAllergies(e.target.value)}
          placeholder="e.g. dairy-free, nut allergy, lactose intolerant — or leave blank."
          className="w-full resize-none rounded-lg border border-wca-gray-light bg-wca-surface p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-wca-red"
        />
        <p className="mt-1 text-xs text-wca-gray-mid">
          Visible to the barista on every order. {allergies.length}/{ALLERGIES_MAX}
        </p>
      </div>

      {error !== null && (
        <p className="text-sm text-wca-red bg-wca-red-light/60 rounded-lg p-3" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-2">
        {onCancel !== undefined && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-5 py-2.5 rounded-lg text-sm font-semibold text-wca-gray-mid hover:text-wca-black disabled:opacity-60"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="bg-wca-red hover:bg-wca-red-dark disabled:bg-wca-gray-mid disabled:cursor-not-allowed text-white font-semibold px-6 py-2.5 rounded-lg min-h-[44px] text-sm transition-colors"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
